/**
 * spaceCameraStore — per-user spatial canvas camera state (pan, zoom).
 *
 * Persistence tiers (fastest → most durable):
 *  1. In-memory Zustand state — immediate UI
 *  2. localStorage per spaceId — restored on reload before CRDT arrives
 *  3. Per-user CRDT 'meta' Y.Map in space:{spaceId}:{userId} — cross-device
 *
 * Each user has their own camera; nobody else's viewport is affected.
 */

import { create } from 'zustand';
import { createFileLogger } from '../utils/logger';

const log = createFileLogger('SPACE_CAMERA_STORE', { enabled: false });

export interface SpaceCamera {
  pan: { x: number; y: number };
  zoom: number;
}

const DEFAULT_CAMERA: SpaceCamera = { pan: { x: 40, y: 40 }, zoom: 0.85 };
const ZOOM_MIN = 0.1;
const ZOOM_MAX = 4;
const LS_PREFIX = 'sc-camera:';
const CRDT_DEBOUNCE_MS = 800;

interface SpaceCameraState {
  cameras: Record<string, SpaceCamera>;
  getCamera: (spaceId: string) => SpaceCamera;
  setCamera: (spaceId: string, camera: SpaceCamera) => void;
  screenToWorld: (camera: SpaceCamera, containerRect: DOMRect, screenX: number, screenY: number) => { x: number; y: number };
  worldToScreen: (camera: SpaceCamera, containerRect: DOMRect, worldX: number, worldY: number) => { x: number; y: number };
  clampZoom: (zoom: number) => number;
  loadFromStorage: (spaceId: string) => SpaceCamera;
  persistToStorage: (spaceId: string, camera: SpaceCamera) => void;
  hydrateFromCRDT: (spaceId: string) => Promise<void>;
}

const debouncedCRDTPersist = (() => {
  const timers = new Map<string, ReturnType<typeof setTimeout>>();
  return (spaceId: string, camera: SpaceCamera) => {
    const existing = timers.get(spaceId);
    if (existing) clearTimeout(existing);
    timers.set(spaceId, setTimeout(async () => {
      timers.delete(spaceId);
      try {
        const { spaceCRDTService } = await import('../services/crdt/spaceCRDTService');
        spaceCRDTService.saveCameraToMeta(spaceId, camera);
      } catch {
        // Non-fatal; localStorage is the fallback
      }
    }, CRDT_DEBOUNCE_MS));
  };
})();

export const useSpaceCameraStore = create<SpaceCameraState>((set, get) => ({
  cameras: {},

  getCamera: (spaceId: string): SpaceCamera => {
    return get().cameras[spaceId] ?? get().loadFromStorage(spaceId);
  },

  setCamera: (spaceId: string, camera: SpaceCamera) => {
    const clamped: SpaceCamera = {
      pan: camera.pan,
      zoom: Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, camera.zoom)),
    };
    set(state => ({ cameras: { ...state.cameras, [spaceId]: clamped } }));
    get().persistToStorage(spaceId, clamped);
    debouncedCRDTPersist(spaceId, clamped);
    log.info('[SpaceCamera] camera updated:', spaceId, clamped);
  },

  // Pure read — never mutates the store, so it is safe to call during render
  // (e.g. the layout's initial useMemo). setCamera/hydrateFromCRDT populate the
  // in-memory cache; uncached reads fall back to localStorage here.
  loadFromStorage: (spaceId: string): SpaceCamera => {
    try {
      const raw = localStorage.getItem(LS_PREFIX + spaceId);
      if (raw) {
        const parsed = JSON.parse(raw) as SpaceCamera;
        if (parsed?.pan && typeof parsed.zoom === 'number') {
          return parsed;
        }
      }
    } catch {
      // Ignore corrupted storage
    }
    return { ...DEFAULT_CAMERA };
  },

  persistToStorage: (spaceId: string, camera: SpaceCamera) => {
    try {
      localStorage.setItem(LS_PREFIX + spaceId, JSON.stringify(camera));
    } catch {
      // Quota exceeded — non-fatal
    }
  },

  /**
   * Try to hydrate camera from CRDT meta (overrides localStorage if found).
   * Called after CRDT space initialization is complete.
   */
  hydrateFromCRDT: async (spaceId: string) => {
    try {
      const { spaceCRDTService } = await import('../services/crdt/spaceCRDTService');
      const crdt = spaceCRDTService.loadCameraFromMeta(spaceId);
      if (crdt) {
        const clamped: SpaceCamera = {
          pan: crdt.pan,
          zoom: Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, crdt.zoom)),
        };
        set(state => ({ cameras: { ...state.cameras, [spaceId]: clamped } }));
        get().persistToStorage(spaceId, clamped);
        log.info('[SpaceCamera] Hydrated from CRDT:', spaceId, clamped);
      }
    } catch {
      // Non-fatal; localStorage value is good enough
    }
  },

  screenToWorld: (camera: SpaceCamera, containerRect: DOMRect, screenX: number, screenY: number) => ({
    x: (screenX - containerRect.left - camera.pan.x) / camera.zoom,
    y: (screenY - containerRect.top - camera.pan.y) / camera.zoom,
  }),

  worldToScreen: (camera: SpaceCamera, containerRect: DOMRect, worldX: number, worldY: number) => ({
    x: worldX * camera.zoom + camera.pan.x + containerRect.left,
    y: worldY * camera.zoom + camera.pan.y + containerRect.top,
  }),

  clampZoom: (zoom: number) => Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, zoom)),
}));

export default useSpaceCameraStore;
